import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "insighto:wizard:draft";

const EMPTY_DRAFT = {
  channel: null,
  config: null,
  fieldValues: {},
  outcomeSelections: {},
  crmChoice: null,
  knowledgeFiles: [], // not serializable to localStorage — files reset on reload
  toolBindings: {}, // { tool_key: tool_user_id } — user's picked LinkToolUser
  connectedTools: [],
  twilio: { channelId: null, phoneNumberSid: null, phoneNumber: null },
  compiledPrompt: null,
};

function loadDraft() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_DRAFT;
    const parsed = JSON.parse(raw);
    return { ...EMPTY_DRAFT, ...parsed };
  } catch {
    return EMPTY_DRAFT;
  }
}

export default function useWizardState() {
  const [draft, setDraft] = useState(loadDraft);
  const [activeStep, setActiveStep] = useState(() => {
    const stored = window.localStorage.getItem(STORAGE_KEY + ":step");
    return stored ? parseInt(stored, 10) || 0 : 0;
  });

  useEffect(() => {
    // Strip the File objects in knowledgeFiles before stringifying — they
    // don't survive JSON serialization and the user will need to re-pick
    // them on reload anyway.
    const { knowledgeFiles, ...persistable } = draft;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(persistable));
  }, [draft]);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY + ":step", String(activeStep));
  }, [activeStep]);

  const update = useCallback((patch) => {
    setDraft((prev) =>
      typeof patch === "function" ? patch(prev) : { ...prev, ...patch }
    );
  }, []);

  const setFieldValue = useCallback((key, value) => {
    setDraft((prev) => ({
      ...prev,
      fieldValues: { ...prev.fieldValues, [key]: value },
    }));
  }, []);

  const toggleOutcome = useCallback((key, on) => {
    setDraft((prev) => ({
      ...prev,
      outcomeSelections: { ...prev.outcomeSelections, [key]: on },
    }));
  }, []);

  const setToolBinding = useCallback((toolKey, toolUserId) => {
    setDraft((prev) => ({
      ...prev,
      toolBindings: { ...prev.toolBindings, [toolKey]: toolUserId },
    }));
  }, []);

  // Seed initial field values + outcome defaults from a fresh WizardConfig.
  const ingestConfig = useCallback((config) => {
    if (!config) return;
    const fieldValues = {};
    (config.fields || []).forEach((f) => {
      fieldValues[f.key] = f.value ?? null;
    });
    const outcomeSelections = {};
    (config.recommended_outcomes || []).forEach((o) => {
      outcomeSelections[o.key] = !!o.default_on;
    });
    setDraft((prev) => ({
      ...prev,
      channel: config.channel,
      config,
      fieldValues: { ...fieldValues, ...prev.fieldValues },
      outcomeSelections: { ...outcomeSelections, ...prev.outcomeSelections },
    }));
  }, []);

  const reset = useCallback(() => {
    window.localStorage.removeItem(STORAGE_KEY);
    window.localStorage.removeItem(STORAGE_KEY + ":step");
    setDraft(EMPTY_DRAFT);
    setActiveStep(0);
  }, []);

  return {
    draft,
    update,
    setFieldValue,
    toggleOutcome,
    setToolBinding,
    ingestConfig,
    reset,
    activeStep,
    setActiveStep,
    goNext: () => setActiveStep((s) => s + 1),
    goBack: () => setActiveStep((s) => Math.max(0, s - 1)),
  };
}
